import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Cpu, Plane, Factory, Shield, Wrench, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const services = [
    {
      icon: Plane,
      title: 'Drone Manufacturing',
      description: 'Custom UAV solutions for industrial applications with advanced control systems',
      link: '/projects/agri-drone'
    },
    {
      icon: Cpu,
      title: 'Semiconductor Solutions',
      description: 'Cutting-edge semiconductor design and manufacturing services',
      link: '/projects/semiconductor'
    },
    {
      icon: Factory,
      title: 'Industrial Automation',
      description: 'Smart factory solutions and industrial process automation',
      link: '/projects/iot-platform'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Rigorous testing and certification for all products',
      link: '/contact'
    }
  ];

  const projects = [
    {
      id: 'agri-drone',
      title: 'Agricultural Drone Fleet',
      description: 'Automated crop monitoring and precision farming solution',
      image: 'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=800'
    },
    {
      id: 'iot-platform',
      title: 'Industrial IoT Platform',
      description: 'Connected factory monitoring and control system',
      image: 'https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&q=80&w=800'
    },
    {
      id: 'semiconductor',
      title: 'Custom Semiconductor Design',
      description: 'Application-specific integrated circuit solutions',
      image: 'https://images.unsplash.com/photo-1601791074012-d4e0ee30d9a7?auto=format&fit=crop&q=80&w=800'
    }
  ];

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <video
          autoPlay
          loop
          muted
          className="absolute w-full h-full object-cover opacity-30"
          style={{ filter: 'brightness(0.4)' }}
        >
          <source src="https://cdn.coverr.co/videos/coverr-computer-chip-closeup-2741/1080p.mp4" type="video/mp4" />
        </video>

        <div className="relative z-10 h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left max-w-3xl"
            >
              <motion.h1 
                className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                Welcome to JK SemiTech
              </motion.h1>
              <motion.p 
                className="text-xl md:text-2xl mb-8 text-gray-300"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                Leading the future of drone technology and semiconductor solutions
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center border border-gray-500 px-8 py-3 rounded-full font-semibold hover:bg-gray-800 transition-all duration-300"
                >
                  Learn More
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
              Our Services
            </h2>
            <p className="text-gray-400 mt-4">Comprehensive solutions for modern industry needs</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="block"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-gray-800 p-6 rounded-xl hover:bg-gray-700 transition-all duration-300 h-full"
                >
                  <service.icon className="h-12 w-12 text-blue-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-400">{service.description}</p>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
              Featured Projects
            </h2>
            <p className="text-gray-400 mt-4">Innovative solutions for real-world challenges</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-800 rounded-xl overflow-hidden group"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                  <p className="text-gray-400 mb-4">{project.description}</p>
                  <Link
                    to={`/projects/${project.id}`}
                    className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Learn More <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Section (Secondary Focus) */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-2xl p-8 md:p-12">
            <div className="md:flex items-center justify-between">
              <div className="mb-8 md:mb-0 md:mr-8">
                <h2 className="text-3xl font-bold mb-4">Professional Training Programs</h2>
                <p className="text-gray-300 mb-6">
                  Enhance your team's capabilities with our industry-focused training programs
                </p>
                <Link
                  to="/courses"
                  className="inline-flex items-center bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                >
                  Explore Programs
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="bg-black/30 p-4 rounded-lg">
                  <Wrench className="h-8 w-8 text-blue-400 mb-2" />
                  <p className="font-semibold">Hands-on Training</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg">
                  <Shield className="h-8 w-8 text-purple-400 mb-2" />
                  <p className="font-semibold">Industry Certified</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;